<template>
  <div class="page-container reveal">
    <el-card class="main-list-card">
      <div class="list-header">
        <div class="header-content">
          <h2 class="page-title">File d'attente (Waitlist)</h2>
          <p class="page-subtitle">{{ requests.length }} demande{{ requests.length !== 1 ? 's' : '' }} en attente d'artisan</p>
        </div>
        <div class="header-actions">
          <el-button @click="loadWaitlist" :loading="isLoading" circle><el-icon><Refresh /></el-icon></el-button>
        </div>
      </div>

      <el-table :data="requests" v-loading="isLoading" class="custom-table" stripe>
        <el-table-column label="Date" width="160">
          <template #default="{ row }">
            {{ row.created_at ? new Date(row.created_at).toLocaleString('fr-FR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="display_id" label="ID" width="120" />
        <el-table-column prop="client_name" label="Client" min-width="150" />
        <el-table-column prop="client_phone" label="Téléphone" width="140" />
        <el-table-column prop="category_name" label="Catégorie" width="180" show-overflow-tooltip />
        <el-table-column label="Statut" width="140">
          <template #default>
            <el-tag type="warning" size="small">En attente</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Actions" width="160" fixed="right">
          <template #default="{ row }">
            <el-button size="small" type="primary" @click="openAssignDialog(row)">Assigner</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog v-model="assignDialogVisible" title="Assigner un artisan manuellement" width="500px" append-to-body>
      <div v-if="selectedRequest" style="margin-bottom: 20px;">
        <p><strong>Demande :</strong> {{ selectedRequest.display_id }} - {{ selectedRequest.category_name }}</p>
        <p><strong>Client :</strong> {{ selectedRequest.client_name }} ({{ selectedRequest.client_phone }})</p>
      </div>

      <el-select
        v-model="selectedArtisanId"
        filterable
        remote
        reserve-keyword
        placeholder="Rechercher un artisan (téléphone, nom)"
        :remote-method="searchArtisans"
        :loading="searchingArtisans"
        style="width: 100%"
      >
        <el-option
          v-for="item in artisanOptions"
          :key="item.id"
          :label="item.full_name + ' (' + item.phone + ')'"
          :value="item.id"
        />
      </el-select>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="assignDialogVisible = false">Annuler</el-button>
          <el-button type="primary" :loading="isAssigning" :disabled="!selectedArtisanId" @click="confirmAssign">
            Confirmer l'assignation
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage, ElNotification } from 'element-plus'
import { Refresh } from '@element-plus/icons-vue'
import { listWaitlistRequests, assignWaitlistRequest, searchArtisans as apiSearchArtisans } from '../services/adminApi'

const isLoading = ref(false)
const requests = ref([])

const assignDialogVisible = ref(false)
const selectedRequest = ref(null)
const selectedArtisanId = ref('')
const artisanOptions = ref([])
const searchingArtisans = ref(false)
const isAssigning = ref(false)

const loadWaitlist = async () => {
  isLoading.value = true
  try {
    requests.value = await listWaitlistRequests()
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || error.message)
  } finally {
    isLoading.value = false
  }
}

const searchArtisans = async (query) => {
  if (query && query.length >= 2) {
    searchingArtisans.value = true
    try {
      artisanOptions.value = await apiSearchArtisans(query)
    } catch (e) {
      ElMessage.error(e.response?.data?.detail || e.message)
    } finally {
      searchingArtisans.value = false
    }
  } else {
    artisanOptions.value = []
  }
}

const openAssignDialog = (row) => {
  selectedRequest.value = row
  selectedArtisanId.value = ''
  artisanOptions.value = []
  assignDialogVisible.value = true
}

const confirmAssign = async () => {
  isAssigning.value = true
  try {
    await assignWaitlistRequest(selectedRequest.value.id, selectedArtisanId.value)
    ElNotification({ type: 'success', title: 'Succès', message: 'Artisan assigné avec succès.' })
    assignDialogVisible.value = false
    loadWaitlist()
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || error.message)
  } finally {
    isAssigning.value = false
  }
}

onMounted(() => {
  loadWaitlist()
})
</script>

<style scoped>
.page-container {
  max-width: 1200px;
  margin: 0 auto;
}
.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}
.page-title {
  margin: 0;
  font-size: 1.5rem;
}
.page-subtitle {
  margin: 0.25rem 0 0;
  color: var(--el-text-color-secondary);
}
</style>
